/**
 * Pickup Slots API service (core-service platform slots S1..S5).
 * Demand rows come from the Fleet backend's SlotPickup mirror; state pushes are
 * relayed by the backend to core's trusted endpoints.
 */

import api from './api';

export type PickupSlotCode = 'S1' | 'S2' | 'S3' | 'S4' | 'S5';

export type PickupSlotState =
  | 'slot_assigned'
  | 'route_included'
  | 'route_appended'
  | 'rolled_over'
  | 'next_day_fallback'
  | 'missed';

export interface SlotBoardRow {
  pickup_uid: string;
  platform_code: string;
  operational_date: string;
  slot_code: PickupSlotCode;
  slot_window: { from: string; to: string } | null;
  preferred_window: { start: string; end: string; crosses_midnight: boolean } | null;
  core_slot_state: PickupSlotState;
  local_slot_state: PickupSlotState;
  merchant_uid: string;
  merchant_name: string;
  address: {
    saved_pick_up_address_uid: string | null;
    lat: number | null;
    lng: number | null;
    region_id: number | null;
    province: string | null;
  } | null;
  order_count: number;
  open_rto_tasks: Array<{
    task_uid: string;
    rto_uid: string;
    task_list_uid: string | null;
    pickup_agent: { uid: string; driver_id: number } | null;
  }>;
  task_uid: string | null;
  is_terminal: boolean;
  last_synced_at: string;
  /** Fleet-side plan: the live route this pickup's task already sits on (null if unplanned). */
  planned_route?: {
    route_id: string;
    route_name: string;
    route_status: string;
    driver_name: string | null;
  } | null;
}

export interface SlotBoardSlot {
  code: PickupSlotCode;
  title: string;
  active?: boolean;
  window: { from: string; to: string };
  close_instant: string;
  closed_at: string | null;
  last_synced_at: string | null;
  counts: Record<string, number> & { total: number };
  cancelled_count?: number;
  generation?: SlotGeneration | null;
  rows: SlotBoardRow[];
}

export interface SlotGenerationZone {
  zone_name: string;
  /**
   * `partial` — routes came out but the solver left tasks behind. Was
   * indistinguishable from `generated` until 2026-08-06, so a run that placed
   * 30 of 100 pickups showed the same green chip as a clean one.
   */
  status: 'generated' | 'partial' | 'no_drivers' | 'failed';
  run_id: string | null;
  demand_count: number;
  order_count: number;
  error?: string | null;
  /** Tasks the solver could not place. 0 on a clean solve. */
  unassigned_count?: number;
  unassigned_task_uids?: string[];
  /**
   * Set when this zone was solved as part of a group (SHIFT1). Zones sharing a
   * group also share a `run_id` — one solve produced all of them.
   */
  group_code?: string | null;
  group_name?: string | null;
}

export interface SlotGeneration {
  status: 'generating' | 'generated' | 'no_demand' | 'failed';
  generated_at: string | null;
  demand_count: number;
  held_count: number;
  last_error?: string | null;
  zones: SlotGenerationZone[];
  /** Total tasks the solver could not place across all zones. */
  unassigned_count?: number;
  /**
   * Zones routed on their own because they carry no zone group while grouping
   * is on. Their work DID go out — nothing here is blocked — so this is the
   * only signal that the grouping silently did not apply.
   */
  ungrouped_zone_names?: string[];
}

/** Shift-mode strip entry: internal driver shift spanning several merchant slots. */
export interface ShiftBoardEntry {
  code: string; // SHIFT1 / SHIFT2
  slot_codes: string[];
  window: { from: string; to: string };
  start_instant: string;
  /** Route generation fires here (shift start − lead minutes, unless overridden). */
  trigger_instant: string;
  /** True when an officer pinned the trigger to a fixed time via the API. */
  trigger_override?: boolean;
  /** Final leg — appends closed past this instant. */
  cutoff_instant: string;
  close_instant: string;
  generation: SlotGeneration | null;
}

export interface SlotBoard {
  operational_date: string;
  slots: SlotBoardSlot[];
  /** Present only in shift mode: generation markers are keyed SHIFT1/SHIFT2, not S1..S5. */
  shifts?: ShiftBoardEntry[];
  closed_day?: { closed: boolean; reason?: string | null; next_open_date?: string };
}

/**
 * Pin a scope's route-generation trigger to a fixed Baghdad time ("HH:mm" 24h).
 * Persistent — applies every operational day until cleared. Server rejects
 * times at/after the scope's append cutoff (422).
 */
export async function setGenerationTrigger(
  code: string,
  triggerTime: string
): Promise<{ code: string; trigger_time: string; trigger_instant: string; note: string }> {
  const response = await api.put<{
    success: boolean;
    data: { code: string; trigger_time: string; trigger_instant: string; note: string };
  }>(`/pickup-slots/generation/${code}/trigger`, { trigger_time: triggerTime });
  return response.data.data;
}

/** Clear the trigger override — back to the derived default (window start − T-lead). */
export async function clearGenerationTrigger(code: string): Promise<{ code: string; cleared: boolean }> {
  const response = await api.delete<{ success: boolean; data: { code: string; cleared: boolean } }>(
    `/pickup-slots/generation/${code}/trigger`
  );
  return response.data.data;
}

export interface SlotConfigSlot {
  code: PickupSlotCode;
  title: string;
  labels: { en: string; ar: string } | null;
  from_time: number; // minutes-of-day 0..1440
  to_time: number;
  cutoff_minutes_before_end: number;
  active: boolean;
  window: { from: string; to: string };
}

export interface SlotConfigMeta {
  source: string; // 'live' | 'cache' | 'fallback'
  fetched_at: string | null;
  operational_day_start?: string;
  timezone?: string;
}

export interface SlotConfigPatch {
  from_time?: number;
  to_time?: number;
  cutoff_minutes_before_end?: number;
  labels?: { en: string; ar: string };
  active?: boolean;
}

export interface ClosedDay {
  closed_on: string; // YYYY-MM-DD
  reason: string | null;
}

export interface AssignmentPanelZone {
  zone_id: string;
  zone_name: string;
  demand_count: number;
  order_count: number;
  required_pa: number;
  available_pa: number;
  available_driver_uids: string[];
  shortfall: number;
  run_id: string | null;
  run_status: string | null;
}

export interface AssignmentPanelSlot {
  /** Slot code in slot mode; SHIFT1/SHIFT2 in shift mode. */
  code: PickupSlotCode | string;
  title: string;
  /** Merchant slot codes this entry covers ([own code] in slot mode). */
  slot_codes?: string[];
  window: { from: string; to: string };
  start_instant: string;
  cutoff_instant: string;
  final_leg: boolean;
  generation_status: string | null;
  zones: AssignmentPanelZone[];
  held_pickup_uids: string[];
}

export interface CouplingSuggestion {
  pickup_uid: string;
  task_uid: string;
  merchant_name: string;
  slot_code: PickupSlotCode;
  order_count: number;
  driver_uid: string;
  driver_name: string | null;
  route_id: string;
  route_name: string;
  route_status: string;
  rto_task_uid: string;
}

export interface SlotResponseInput {
  choice: 'accept' | 'change' | 'defer';
  time_slot_code?: PickupSlotCode;
  responded_at: string;
  channel: 'whatsapp' | 'push';
  preferred_timing?: 'anytime' | 'early' | 'mid' | 'late' | null;
}

export interface OverrideDailyLimitInput {
  merchant_uid: string;
  order_uids: string[];
  saved_pick_up_address_uid?: string;
  time_slot_code?: PickupSlotCode;
  reason: string;
  /** MOVE orders out of the pickup they are attached to. Core rejects them otherwise. */
  force?: boolean;
}

/** An order core would not take, with the reason it named. */
export interface OverrideRejectedOrder {
  uid: string;
  reason?: string;
  message?: string;
  status_slug?: string;
  pick_up_platform_code?: string;
}

/** A pickup whose orders were moved into the new one. */
export interface OverrideSourcePickup {
  uid?: string;
  platform_code?: string;
  remaining_order_count?: number;
  moved_order_uids?: string[];
}

export interface OverrideCreatedPickup {
  uid?: string;
  platform_code?: string;
  /** "next_day_fallback" means core pushed it to tomorrow — the slot had closed. */
  slot_state?: string;
  operational_date?: string;
}

/**
 * Longest reason core will store. It writes the reason into
 * `pick_up_slot_logs.rule`, a 64-char column, and neither validates nor
 * truncates — a longer one throws MySQL 1406 and surfaced the raw SQL, order
 * uids and all, to the officer. Measured against core-stg: 64 created the
 * pickup, 65 threw. The backend enforces the same number
 * (`utils/overrideReason.ts`) and translates an overflow that still gets
 * through; this copy stops the officer typing past it in the first place.
 */
export const OVERRIDE_REASON_MAX = 64;

export interface OverrideDailyLimitResult {
  pickups: OverrideCreatedPickup[];
  rejected: OverrideRejectedOrder[];
  source_pickups: OverrideSourcePickup[];
}

export async function getSlotBoard(operationalDate?: string, refresh?: boolean): Promise<SlotBoard> {
  const params = new URLSearchParams();
  if (operationalDate) params.append('operational_date', operationalDate);
  if (refresh) params.append('refresh', 'true');
  const response = await api.get<{ success: boolean; data: SlotBoard }>(
    `/pickup-slots/board?${params.toString()}`
  );
  return response.data.data;
}

export async function closeSlot(
  slotCode: PickupSlotCode,
  operationalDate: string
): Promise<{ rolled: number; fallback: number; skipped: number; alreadyClosed: boolean }> {
  const response = await api.post<{
    success: boolean;
    data: { rolled: number; fallback: number; skipped: number; alreadyClosed: boolean };
  }>(`/pickup-slots/${slotCode}/close`, { operational_date: operationalDate });
  return response.data.data;
}

export async function relaySlotResponse(
  pickupUid: string,
  input: SlotResponseInput
): Promise<{ superseded: boolean; is_next_day?: boolean }> {
  const response = await api.post<{
    success: boolean;
    data: { superseded: boolean; is_next_day?: boolean };
  }>(`/pickup-slots/pickups/${pickupUid}/slot-response`, input);
  return response.data.data;
}

export async function markMissed(pickupUid: string, operationalDate: string): Promise<void> {
  await api.post<{ success: boolean; data: { event_id: string | null; status: string | null } }>(
    `/pickup-slots/pickups/${pickupUid}/mark-missed`,
    { operational_date: operationalDate }
  );
}

export async function rollToNextSlot(
  pickupUid: string,
  operationalDate: string
): Promise<{ event_id: string; status: string; new_time_slot_code: PickupSlotCode }> {
  const response = await api.post<{
    success: boolean;
    data: { event_id: string; status: string; new_time_slot_code: PickupSlotCode };
  }>(`/pickup-slots/pickups/${pickupUid}/roll-over`, { operational_date: operationalDate });
  return response.data.data;
}

export async function overrideDailyLimit(
  input: OverrideDailyLimitInput
): Promise<OverrideDailyLimitResult> {
  const response = await api.post<{
    success: boolean;
    data: unknown;
    rejected?: OverrideRejectedOrder[];
    source_pickups?: OverrideSourcePickup[];
  }>('/pickup-slots/override-daily-limit', input);
  const { data, rejected, source_pickups } = response.data;
  return {
    // core returns an array of created pickups; tolerate a single object
    pickups: Array.isArray(data) ? (data as OverrideCreatedPickup[]) : data ? [data as OverrideCreatedPickup] : [],
    rejected: rejected ?? [],
    source_pickups: source_pickups ?? [],
  };
}

/** Resolution of a task code (BXT-…, as shown on the Tasks page) or a pickup code (BXPU-…). */
export interface PickupOrdersLookup {
  pickup: { uid: string; platform_code: string; cancelled: boolean };
  merchant: { uid: string; name: string } | null;
  orders: Array<{ uid: string; platform_code: string; size: string; cancelled: boolean }>;
}

export async function getPickupOrders(platformCode: string): Promise<PickupOrdersLookup> {
  const response = await api.get<{ success: boolean; data: PickupOrdersLookup }>(
    `/pickup-slots/pickup-orders/${encodeURIComponent(platformCode.trim())}`
  );
  return response.data.data;
}

/**
 * The shift grouping as FLEET config has it (`SHIFT_SLOT_MAP`), not core's.
 * Empty when shift routing is off — callers must treat "no shifts" as "do not
 * offer a shift control", never as a reason to fall back to a hardcoded map.
 */
export interface SlotConfigShift {
  code: string; // SHIFT1 / SHIFT2
  slot_codes: string[];
  window: { from: string; to: string };
}

export async function getSlotConfig(): Promise<{
  slots: SlotConfigSlot[];
  shifts: SlotConfigShift[];
  meta: SlotConfigMeta;
}> {
  const response = await api.get<{
    success: boolean;
    data: { slots: SlotConfigSlot[]; shifts?: SlotConfigShift[]; meta: SlotConfigMeta };
  }>('/pickup-slots/config');
  return { ...response.data.data, shifts: response.data.data.shifts ?? [] };
}

export async function patchSlotConfig(code: PickupSlotCode, patch: SlotConfigPatch): Promise<SlotConfigSlot> {
  const response = await api.patch<{ success: boolean; data: SlotConfigSlot }>(
    `/pickup-slots/config/${code}`,
    patch
  );
  return response.data.data;
}

export async function getClosedDays(from?: string, to?: string): Promise<ClosedDay[]> {
  const params = new URLSearchParams();
  if (from) params.append('from', from);
  if (to) params.append('to', to);
  const response = await api.get<{ success: boolean; data: { closed_days: ClosedDay[] } }>(
    `/pickup-slots/closed-days?${params.toString()}`
  );
  return response.data.data.closed_days;
}

export async function addClosedDay(closedOn: string, reason?: string): Promise<ClosedDay> {
  const response = await api.post<{ success: boolean; data: ClosedDay }>('/pickup-slots/closed-days', {
    closed_on: closedOn,
    ...(reason ? { reason } : {}),
  });
  return response.data.data;
}

export async function removeClosedDay(date: string): Promise<void> {
  await api.delete(`/pickup-slots/closed-days/${date}`);
}

export async function getAssignmentPanel(operationalDate: string): Promise<AssignmentPanelSlot[]> {
  const response = await api.get<{ success: boolean; data: { slots: AssignmentPanelSlot[] } }>(
    `/pickup-slots/assignment-panel?operational_date=${operationalDate}`
  );
  return response.data.data.slots;
}

export async function getCouplingSuggestions(
  operationalDate?: string,
  refresh?: boolean
): Promise<CouplingSuggestion[]> {
  const params = new URLSearchParams();
  if (operationalDate) params.append('operational_date', operationalDate);
  if (refresh) params.append('refresh', 'true');
  const response = await api.get<{ success: boolean; data: { suggestions: CouplingSuggestion[] } }>(
    `/pickup-slots/coupling-suggestions?${params.toString()}`
  );
  return response.data.data.suggestions;
}
